/**
 * Polymarket trade economics — the client-approved Risk / To Win / Juice
 * contract for every Polymarket accounting surface (trade cards, position
 * views, and the relay's sheet export mirror this module; the relay keeps
 * its own copy in relay/src/services/polyEconomics.ts because frontend and
 * relay do not share code).
 *
 * Convention (client + accounting VA, 2026-07-21/22 — supersedes the
 * earlier "all-in" derivation that put the fee in both legs):
 *
 *   cleanCost = totalIn − fees      // what actually bought contracts
 *   risk      = totalIn             // fee-INCLUSIVE: all money out the door
 *   toWin     = qty − cleanCost     // fee-free winnings ($1/contract payout;
 *                                   // Polymarket settlement charges no fee)
 *   juice     = American odds of risk vs toWin
 *
 * The opening fee is counted exactly once, in the juice: you lay `risk` to
 * clear `toWin`, so the fee drags the line (e.g. −104.1 vig-free → −104.8
 * effective) without being double-charged against the winnings. The invariant
 * `cleanCost + toWin = qty × $1` holds; `risk + toWin` intentionally exceeds
 * the settlement payout by the fee.
 *
 * Worked example (Athens Open, settled NO — the canonical fixture):
 *   qty 5,881.7139 · fees $20.18 · totalIn $3,019.85
 *   → risk $3,019.85 · cleanCost $2,999.67 · toWin $2,882.04 · juice −104.78
 *
 * All inputs are dollars (card-VM units). Fail closed: unknown fee evidence
 * (null) yields all-null economics — never a fabricated line.
 */

import { centsToAmericanOdds, formatAmericanOdds } from './american';

export interface PolyTradeEconomics {
  /** Fee-inclusive dollars committed at entry (= total in). */
  riskDollars: number | null;
  /** Fee-exclusive cost basis (totalIn − fees). */
  cleanCostDollars: number | null;
  /** Fee-free winnings on a favorable settlement: qty − cleanCost. */
  toWinDollars: number | null;
  /** American odds of laying `risk` to clear `toWin`, rounded to `decimals`. */
  juiceOdds: number | null;
  /** Formatted juice ("−104.78"), em-dash when undefined. */
  juice: string;
}

const NULL_ECONOMICS: PolyTradeEconomics = {
  riskDollars: null,
  cleanCostDollars: null,
  toWinDollars: null,
  juiceOdds: null,
  juice: '—',
};

/**
 * Derive the trio from a row's exact quantity, fee-inclusive total-in, and
 * the opening fees contained in it. `decimals` defaults to 2 so the value
 * can be transcribed to the accounting sheet without re-deriving from
 * rounded dollar labels.
 */
export function polyTradeEconomics(args: {
  /** Exact (fractional) contract count — each pays $1 on settlement. */
  qty: number;
  /** Fee-inclusive dollars committed at entry. */
  totalInDollars: number | null;
  /** Opening fees included in totalIn. Null = unknown, 0 = known-free. */
  feesDollars: number | null;
  decimals?: number;
}): PolyTradeEconomics {
  const { qty, totalInDollars, feesDollars, decimals = 2 } = args;
  if (
    !Number.isFinite(qty) ||
    qty <= 0 ||
    totalInDollars == null ||
    !Number.isFinite(totalInDollars) ||
    totalInDollars < 0 ||
    feesDollars == null ||
    !Number.isFinite(feesDollars) ||
    feesDollars < 0
  ) {
    return NULL_ECONOMICS;
  }

  const cleanCost = totalInDollars - feesDollars;
  const toWin = qty - cleanCost;
  if (cleanCost < 0 || toWin <= 0 || totalInDollars <= 0) return NULL_ECONOMICS;

  // risk : toWin as an implied price keeps the American-odds boundary and
  // even-money conventions of the canonical converter (american.ts).
  const impliedCents = (100 * totalInDollars) / (totalInDollars + toWin);
  const juiceOdds = centsToAmericanOdds(impliedCents, decimals);
  return {
    riskDollars: totalInDollars,
    cleanCostDollars: cleanCost,
    toWinDollars: toWin,
    juiceOdds,
    juice: formatAmericanOdds(juiceOdds, decimals),
  };
}
